import React from "react";

import useFetch from "commons/hooks/useFetch";

import type {
    RequestType,
    RequestWithBodyType,
    ResponseType,
} from "commons/commons-fetch/types/http";

export type HookParamsType = {
    baseUrl: string;
    accessTokenGetter: () => string | undefined;
};

function useHttpBase({ baseUrl, accessTokenGetter }: HookParamsType) {
    const { get, post, patch, put, del } = useFetch();

    const getManaged = React.useCallback(
        <P, Q, R>({
            url,
            pathParams,
            queryParams,
            headers,
            options,
        }: RequestType<P, Q>): ResponseType<R> => {
            return get<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                accessToken: accessTokenGetter(),
                headers,
                options,
            });
        },
        [get, accessTokenGetter, baseUrl]
    );

    const delManaged = React.useCallback(
        <P, Q, R>({
            url,
            pathParams,
            queryParams,
            headers,
            options,
        }: RequestType<P, Q>): ResponseType<R> => {
            return del<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                accessToken: accessTokenGetter(),
                headers,
                options,
            });
        },
        [del, accessTokenGetter, baseUrl]
    );

    const postManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return post<P, Q, B, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                body,
                accessToken: accessTokenGetter(),
                headers,
                options,
            });
        },
        [post, accessTokenGetter, baseUrl]
    );

    const patchManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return patch<P, Q, B, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                body,
                accessToken: accessTokenGetter(),
                headers,
                options,
            });
        },
        [patch, accessTokenGetter, baseUrl]
    );

    const putManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return put<P, Q, B, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                body,
                accessToken: accessTokenGetter(),
                headers,
                options,
            });
        },
        [put, accessTokenGetter, baseUrl]
    );

    return React.useMemo(
        () => ({
            get: getManaged,
            del: delManaged,
            post: postManaged,
            patch: patchManaged,
            put: putManaged,
        }),
        [getManaged, delManaged, postManaged, patchManaged, putManaged]
    );
}

export default useHttpBase;
